import { useCallback } from 'react';

import { LoadMoreSentinel } from '@/components/shared/load-more-sentinel';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { usePagesActions, usePagesState } from '@/stores/pages-store';
import type { Page } from '@/types/page';
import type { PaginationInfo } from '@/types/pagination';
import { SortablePageTree } from './sortable-page-tree';

type PageTreeScope = 'navigation' | 'non_navigation';

interface PageTreeScopeListProps {
  scope: PageTreeScope;
  onPageClick: (page: Page) => void;
  searchQuery?: string;
  pages?: Page[];
  isSearching?: boolean;
  isSearchLoading?: boolean;
  isActive?: boolean;
}

const SCOPE_COPY = {
  navigation: {
    noResults: 'No pages match your search.',
    noPages: 'No pages exist yet.',
  },
  nonNavigation: {
    noResults: 'No non-navigation pages match your search.',
    noPages: 'No non-navigation pages exist yet.',
  },
} as const;

function scopeCopyFor(scope: PageTreeScope) {
  return scope === 'navigation' ? SCOPE_COPY.navigation : SCOPE_COPY.nonNavigation;
}

function selectScopeState(
  scope: PageTreeScope,
  state: ReturnType<typeof usePagesState>,
): {
  treePages: Page[];
  pagination: PaginationInfo;
} {
  if (scope === 'navigation') {
    return {
      treePages: state.mainNavigationTree,
      pagination: state.mainNavigationPagination,
    };
  }

  return {
    treePages: state.nonNavigationTree,
    pagination: state.nonNavigationPagination,
  };
}

export function PageTreeScopeList({
  scope,
  onPageClick,
  searchQuery = '',
  pages,
  isSearching = false,
  isSearchLoading = false,
  isActive = true,
}: PageTreeScopeListProps) {
  const pagesState = usePagesState();
  const { treePages, pagination } = selectScopeState(scope, pagesState);
  const { reorderPage, loadMorePages } = usePagesActions();
  const { isLoading } = pagesState;

  const hasMorePages = isActive && !isSearching && pagination.currentPage < pagination.totalPages;
  const handleLoadMore = useCallback(() => loadMorePages(scope), [loadMorePages, scope]);
  const { sentinelRef, isLoadingMore } = useInfiniteScroll({
    hasNextPage: hasMorePages,
    isLoading: isLoading || isSearchLoading,
    onLoadMore: handleLoadMore,
    rootMargin: '100px',
    threshold: 0.1,
  });

  return (
    <div className="flex flex-col gap-2">
      <SortablePageTree
        pages={isSearching ? (pages ?? []) : treePages}
        isLoading={isLoading || isSearchLoading}
        searchQuery={searchQuery}
        emptyStateMessages={scopeCopyFor(scope)}
        onPagesReorder={reorderPage}
        onOpenNestedView={onPageClick}
      />
      <LoadMoreSentinel
        sentinelRef={sentinelRef}
        isLoadingMore={isLoadingMore}
        hasNextPage={hasMorePages}
        className="py-2"
      />
    </div>
  );
}
